import { Building2, ChevronRight, Sparkles } from "lucide-react"
import type { ComponentType } from "react"
import { useTranslation } from "react-i18next"

import { SUPPORT_MAILTO } from "@/lib/support"

export type LoginRole = "brand" | "ambassador"

interface RoleTileProps {
  icon: ComponentType<{ className?: string }>
  title: string
  subtitle: string
  onSelect: () => void
  "data-testid": string
}

/**
 * A single role tile — a full-width button (the whole tile is the tap
 * target, ≥44px tall). Leading 44px icon square, title + subtitle, a
 * trailing chevron that nudges right on hover (desktop). Keyboard
 * focusable; Enter/Space activate (native `<button>` semantics).
 */
function RoleTile({
  icon: Icon,
  title,
  subtitle,
  onSelect,
  "data-testid": testId,
}: RoleTileProps) {
  return (
    <button
      type="button"
      onClick={onSelect}
      data-testid={testId}
      aria-label={`${title}, ${subtitle}`}
      className="group border-border focus-visible:ring-ring flex w-full items-center gap-4 rounded-lg border p-4 text-left transition-[border-color,box-shadow] hover:border-[var(--accent-primary)] hover:shadow-sm focus-visible:border-[var(--accent-primary)] focus-visible:ring-2 focus-visible:outline-none"
    >
      <span
        aria-hidden="true"
        className="flex size-11 shrink-0 items-center justify-center rounded-md bg-[var(--accent-primary-muted)] text-[var(--accent-primary)]"
      >
        <Icon className="size-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="text-foreground block text-sm font-medium">
          {title}
        </span>
        <span className="text-muted-foreground block text-sm">{subtitle}</span>
      </span>
      <ChevronRight
        aria-hidden="true"
        className="text-muted-foreground size-5 shrink-0 transition-transform md:group-hover:translate-x-0.5"
      />
    </button>
  )
}

/**
 * Footer helper shared by the role-select + ambassador panels:
 * "Need access? Ask your brand for an invite · Contact support" where
 * "Contact support" is a `mailto:` to the platform support address.
 */
export function LoginSupportHelper() {
  const { t } = useTranslation()
  return (
    <p className="text-muted-foreground text-center text-sm">
      {t("login.help_prefix")}{" "}
      <a
        href={SUPPORT_MAILTO}
        className="text-foreground underline decoration-neutral-300 underline-offset-4 hover:decoration-current"
        data-testid="login-support-link"
      >
        {t("login.help_support_link")}
      </a>
    </p>
  )
}

export interface RoleGateProps {
  /** Called with the chosen role when a tile is activated. The parent
   *  swaps panels client-side (no reload). */
  onSelect: (role: LoginRole) => void
}

/**
 * RoleGate — OKS-475 first panel of the returning-login surface
 * (UX Spec §2 role-select). Asks "Brand or Ambassador?" before any
 * sign-in method is shown, so a Brand never sees the TikTok button and
 * a creator never sees a password field. Pure presentation: selection
 * is lifted to the parent via `onSelect`.
 */
export default function RoleGate({ onSelect }: RoleGateProps) {
  const { t } = useTranslation()
  return (
    <div className="flex flex-col gap-6" data-testid="login-role-gate">
      <div className="space-y-1 text-center">
        <h1 className="text-xl font-medium">{t("login.role_select.title")}</h1>
        <p className="text-muted-foreground text-sm">
          {t("login.role_select.subtitle")}
        </p>
      </div>

      <div className="flex flex-col gap-3">
        <RoleTile
          icon={Building2}
          title={t("login.role_select.brand_title")}
          subtitle={t("login.role_select.brand_subtitle")}
          onSelect={() => onSelect("brand")}
          data-testid="role-tile-brand"
        />
        <RoleTile
          icon={Sparkles}
          title={t("login.role_select.ambassador_title")}
          subtitle={t("login.role_select.ambassador_subtitle")}
          onSelect={() => onSelect("ambassador")}
          data-testid="role-tile-ambassador"
        />
      </div>

      <LoginSupportHelper />
    </div>
  )
}
